import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SsrService } from '@hrra/core';
import { LocaleService } from './locale.service';

/**
 * Guards the `**` wildcard route. A URL that carries the default locale as a
 * redundant prefix (e.g. `/ka/jobs` when `ka` is the default) lands here,
 * because the default language uses base href `/`, so the router cannot match
 * `/ka/...`. Such URLs are 301-redirected to their canonical, prefix-less form;
 * genuinely unmatched URLs fall through to the 404 component.
 *
 * Running as a guard (before the component) means the redirect status is set
 * before NotExistingPageComponent would otherwise set 404.
 */
export const localeRedirectGuard: CanActivateFn = (route, state) => {
  const localeService = inject(LocaleService);
  const ssr = inject(SsrService);
  const router = inject(Router);

  const redirectUrl = localeService.getDefaultPrefixRedirect(state.url);

  if (redirectUrl === null) {
    return true; // genuine 404 — let NotExistingPageComponent render
  }

  if (ssr.isPlatformServer()) {
    ssr.setStatusMovedPermanently(redirectUrl); // 301 + Location header, skip render
    return false;
  }

  return router.parseUrl(redirectUrl); // browser: client-side redirect to canonical URL
};
